import { Title } from '@solidjs/meta'
import { useParams } from '@solidjs/router'
import { type Component, createResource, createSignal, Show } from 'solid-js'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Icon from '~/components/Icon'
import FormRenderer from '~/layers/FormRenderer'
import { formService } from '~/services/form.service'
import { resolveError } from '~/utils/error'

/**
 * Página pública del formulario: la abre cualquiera con el enlace, sin sesión.
 * No comparte layout con el administrador ni con la landing a propósito — quien
 * llega aquí sólo tiene que responder.
 *
 * Todo lo que pinta los campos vive en FormRenderer, que es el mismo que usa la
 * previsualización del constructor.
 */
const PublicFormPage: Component = () => {
  const params = useParams()

  /**
   * El título de la pestaña vive en una señal y no en el recurso: solid-meta
   * relee los hijos de <Title> al desmontar, y para entonces el recurso ya está
   * desechado. Una señal suelta sí se puede leer.
   */
  const [documentTitle, setDocumentTitle] = createSignal('Formulario | Florez Jaramillo')

  const [form] = createResource(
    () => params.slug,
    async (slug: string) => {
      try {
        const data = await formService.getPublic(slug)
        setDocumentTitle(`${data.title} | Florez Jaramillo`)
        return data
      } catch {
        return null
      }
    }
  )

  const [isSending, setIsSending] = createSignal(false)
  const [sent, setSent] = createSignal<string | null>(null)
  const [globalError, setGlobalError] = createSignal('')
  const [serverErrors, setServerErrors] = createSignal<Record<string, string>>({})

  const isClosed = () => form()?.status === 'closed'

  const handleSubmit = async (answers: Record<string, unknown>) => {
    setGlobalError('')
    setServerErrors({})
    setIsSending(true)

    try {
      const result = await formService.submit(params.slug ?? '', answers)
      setSent(result.successMessage || '¡Gracias! Tu respuesta quedó registrada.')
    } catch (err: unknown) {
      // El backend devuelve un mapa campo -> mensaje para marcar cada pregunta.
      const fieldErrors = (err as { response?: { data?: { errors?: Record<string, string> } } })?.response?.data?.errors
      if (fieldErrors && typeof fieldErrors === 'object') {
        setServerErrors(fieldErrors)
        setGlobalError('Revisa los campos marcados.')
      } else {
        setGlobalError(resolveError(err))
      }
    } finally {
      setIsSending(false)
    }
  }

  return (
    <>
      <Title>{documentTitle()}</Title>

      <div class="min-h-dvh bg-surface-container py-8 sm:py-12">
        <Container class="max-w-3xl">
          <Show when={form.loading}>
            <div class="py-16 text-center text-foreground-subtle">Cargando formulario...</div>
          </Show>

          <Show when={form.error}>
            <Card>
              <Card.Body>
                <div class="py-12 text-center">
                  <Icon icon="material-symbols-light:link-off" size={48} class="mx-auto mb-3 text-foreground-subtle" />
                  <h1 class="text-lg font-semibold text-foreground">Formulario no disponible</h1>
                  <p class="text-sm text-foreground-subtle mt-1">{resolveError(form.error)}</p>
                </div>
              </Card.Body>
            </Card>
          </Show>

          <Show when={form() && !form.error}>
            <Show
              when={!sent()}
              fallback={
                <Card>
                  <Card.Body>
                    <div class="py-12 text-center">
                      <Icon icon="material-symbols-light:check-circle-outline" size={56} class="mx-auto mb-3 text-success" />
                      <h1 class="text-xl font-bold text-foreground">¡Listo!</h1>
                      <p class="text-sm text-foreground-subtle mt-2 whitespace-pre-line">{sent()}</p>
                    </div>
                  </Card.Body>
                </Card>
              }
            >
              <Card>
                <Card.Header>
                  <div>
                    <h1 class="text-2xl font-bold text-foreground">{form()!.title}</h1>
                    <Show when={form()!.description}>
                      <p class="text-sm text-foreground-subtle mt-1 whitespace-pre-line">{form()!.description}</p>
                    </Show>
                  </div>
                </Card.Header>
                <Card.Body>
                  <Show
                    when={!isClosed()}
                    fallback={
                      <div class="py-10 text-center text-foreground-subtle">
                        <Icon icon="material-symbols-light:lock-outline" size={40} class="mx-auto mb-2" />
                        <p>Este formulario ya no admite respuestas.</p>
                      </div>
                    }
                  >
                    <FormRenderer
                      fields={form()!.fields ?? []}
                      steps={form()!.steps}
                      settings={form()!.settings}
                      serverErrors={serverErrors()}
                      globalError={globalError()}
                      submitting={isSending()}
                      onSubmit={handleSubmit}
                    />
                  </Show>
                </Card.Body>
              </Card>
            </Show>

            <p class="text-center text-xs text-foreground-subtle mt-6">Formulario creado con Florez Jaramillo</p>
          </Show>
        </Container>
      </div>
    </>
  )
}

export default PublicFormPage
